import { useEffect, useState } from "react";
import axios, { API } from "../../api";

/**
 * Phase 25.4 — Beta Access Request form for Snap AR Lenses and Spotify integration.
 *
 * Background:
 *   - Snap Camera Kit requires us to manually register each user's Snap profile
 *     during development (we paste their profile name into Snap's dev dashboard).
 *   - Spotify Web API in development mode only allows ~5 pre-allowlisted user emails.
 *
 * Until both move out of dev mode, this form lets users self-serve a request that:
 *   (a) saves the request in our `beta_access_requests` collection so the founder
 *       can batch-approve in the admin queue
 *   (b) auto-emails support@wwwwhynot.com with their handle so the founder doesn't
 *       have to dig through tickets
 *   (c) shows the user a clear "we'll get to you within 24-48 hours" message
 *
 * Idempotent — re-opening shows their submitted handles + current status.
 */
export function BetaAccessModal({ onClose }) {
  const [snapHandle, setSnapHandle] = useState("");
  const [spotifyEmail, setSpotifyEmail] = useState("");
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/beta-access/me`);
        if (res.data) {
          setExisting(res.data);
          setSnapHandle(res.data.snap_handle || "");
          setSpotifyEmail(res.data.spotify_email || "");
        }
      } catch (e) {
        // Endpoint may not exist on older deploys — silent.
      }
    })();
  }, []);

  const handleSubmit = async () => {
    const snap = snapHandle.trim();
    const spotify = spotifyEmail.trim().toLowerCase();
    if (!snap && !spotify) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/beta-access/request`, {
        snap_handle: snap || null,
        spotify_email: spotify || null,
      });
      setSubmitted(true);
    } catch (e) {
      console.error("beta-access submit", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="beta-access-overlay">
      <div className="beta-access-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-header">
          <h2>🎁 Request Beta Access</h2>
          <button className="journal-close" onClick={onClose} data-testid="beta-access-close">×</button>
        </div>
        <p className="journal-subtitle">
          Aura&apos;s Spotify &amp; Snap AR lens features are in private beta. We&apos;ll get you
          added by hand within 24-48 hours.
        </p>

        {!submitted && (
          <>
            <label className="beta-access-label">
              <span className="beta-access-icon">📸</span>
              <span>
                <strong>Snap profile name</strong>
                <small>For AR Lenses. Find it in Snapchat → your bumoji → Settings → Username.</small>
              </span>
            </label>
            <input
              type="text"
              className="init-input"
              placeholder="@yoursnapname"
              value={snapHandle}
              onChange={(e) => setSnapHandle(e.target.value)}
              autoComplete="off"
              data-testid="beta-snap-input"
            />

            <label className="beta-access-label">
              <span className="beta-access-icon">🎵</span>
              <span>
                <strong>Spotify account email</strong>
                <small>For Aura to create playlists. Only 5 slots open right now — first come, first served.</small>
              </span>
            </label>
            <input
              type="email"
              className="init-input"
              placeholder="you@example.com"
              value={spotifyEmail}
              onChange={(e) => setSpotifyEmail(e.target.value)}
              autoComplete="off"
              data-testid="beta-spotify-input"
            />

            <div className="beta-access-cautions">
              <p>
                <strong>Heads up:</strong> Spotify may glitch occasionally during prototype — that&apos;s an access-quota
                hiccup, not a code bug. It&apos;ll resolve when we move out of dev mode.
              </p>
            </div>

            <button
              className="init-btn"
              onClick={handleSubmit}
              disabled={submitting || (!snapHandle.trim() && !spotifyEmail.trim())}
              data-testid="beta-access-submit"
            >
              {submitting ? "Saving…" : "Request access"}
            </button>

            <p className="beta-access-fineprint">
              Or just email <a href="mailto:support@wwwwhynot.com?subject=Aura%20Beta%20Access%20Request">support@wwwwhynot.com</a> directly.
            </p>
          </>
        )}

        {submitted && (
          <div className="beta-access-success" data-testid="beta-access-success">
            <h3>You&apos;re queued. 💜</h3>
            <p>
              We got it. Both you and our inbox got a copy. Expect access within 24-48 hours.
              You&apos;ll get an email the moment you&apos;re added.
            </p>
          </div>
        )}

        {existing && !submitted && (
          <div className="beta-access-status" data-testid="beta-access-existing-status">
            <small>
              Existing request: Snap = <strong>{existing.snap_status || "—"}</strong> ·
              Spotify = <strong>{existing.spotify_status || "—"}</strong>
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

export default BetaAccessModal;
